import React, { useContext, useEffect, useState } from "react";
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Checkbox,
  FormControlLabel,
  Box,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "../../../contexts/InfoContext";
import { useAuth } from "../../../contexts/AuthContext";
import fetchData from "../../../api/services/Users/getUser";
import getInternationalDate from "../../../utils/getDate";
import { SpinnerContext } from "../../../contexts/SpinnerContext";
import CustomTextField from "../../VersatileComponents/orderTextInput";
import update from "../../../api/orders/edit";
import { getOrderTypes } from "../../../api/services/orderTypes/orderTypes";
import checkCustomerMembership from "../../../api/customer/checkCustomer";
import checkCustomerBlock from "../../../api/customer/checkCustomerBlock";

// Validation schema
const EditAsbezaOrderFormValidationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  blockHouse: Yup.string().required("Block House is required"),
  phone: Yup.string().required("Phone is required"),
  deliveryguyId: Yup.string().required("Please select a Delivery Guy"),
  branchId: Yup.string().required("Please select a Branch"),
  customerType: Yup.string().required("Customer Type is required"),
  price: Yup.number().when("customerType", {
    is: (value) => value === "negotiable",
    then: () =>
      Yup.number()
        .required("Price is required for negotiable orders")
        .positive("Price must be positive"),
    otherwise: () => Yup.number().nullable(),
  }),
  order: Yup.array()
    .of(
      Yup.object().shape({
        isCustom: Yup.boolean().required(),
        category: Yup.string().when("isCustom", {
          is: false,
          then: () => Yup.string().required("Category is required"),
          otherwise: () => Yup.string().nullable(),
        }),
        type: Yup.string().required("Order type is required"),
        measurement: Yup.string().required("Measurement is required"),
        quantity: Yup.number()
          .required("Quantity is required")
          .positive("Quantity must be positive"),
        additionalInfo: Yup.string().nullable(), // Optional field
      })
    )
    .min(1, "At least one order item is required"),
});

const DEBOUNCE_DELAY = 1000; // milliseconds

const EditAsbezaOrderForm = ({
  data,
  isEditDialogOpen,
  closeEditDialog,
  fromWhere,
}) => {
  const { openSnackbar } = useSnackbar();
  const { user } = useAuth();
  const theme = useTheme();
  const { setIsSubmitting } = useContext(SpinnerContext);
  const [branches, setBranches] = useState([]);
  const [deliveryGuy, setDeliveryGuy] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customerType, setCustomerType] = useState(
    data.customerType || "regular"
  );
  const [isMembership, setIsMembership] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false); // State for block status

  // Fetch branches and delivery guys
  useEffect(() => {
    const unsubscribe = fetchData("branches", setBranches);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = fetchData("Deliveryturn", setDeliveryGuy);
    return () => unsubscribe();
  }, []);

  // Fetch categories and order types
  useEffect(() => {
    getOrderTypes()
      .then((categoriesData) => setCategories(categoriesData || []))
      .catch((error) => {
        console.error("Error fetching categories:", error);
        openSnackbar("Failed to fetch categories.", "error");
      });
  }, [openSnackbar]);

  // Initialize formik
  const formik = useFormik({
    initialValues: {
      name: data.name || "",
      phone: data.phone || "",
      blockHouse: data.blockHouse || "",
      additionalInfo: fromWhere === "edit" ? data.additionalInfo || "" : "",
      order:
        fromWhere === "edit" && data.order && data.order.length
          ? data.order.map((item) => ({
              isCustom: item.isCustom || false, // Default to false for legacy data
              category: item.category || "",
              type: item.type || (typeof item === "string" ? item : ""),
              measurement: item.measurement || "",
              quantity: item.quantity || "",
              additionalInfo: item.additionalInfo || "",
            }))
          : [],
      deliveryguyId: fromWhere === "edit" ? data.deliveryguyId || "" : "",
      deliveryguyName: fromWhere === "edit" ? data.deliveryguyName || "" : "",
      branchId: fromWhere === "edit" ? data.branchId || "" : "",
      branchName: fromWhere === "edit" ? data.branchName || "" : "",
      activeTable: fromWhere === "edit" ? data.activeTable || "" : "",
      active: fromWhere === "edit" ? data.active || "" : "",
      activeDailySummery:
        fromWhere === "edit" ? data.activeDailySummery || "" : "",
      customerType: data.customerType || "regular",
      price: data.price || "",
      deliveryType: "regular",
    },
    validationSchema: EditAsbezaOrderFormValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const date = getInternationalDate();
        values.date = date;
        values.callcenterId = user.uid;
        values.customerType = customerType;
        values.deliveryType = "regular";

        if (!values.deliveryguyId || !values.deliveryguyName) {
          handleCloseForm();
          const error = new Error(
            "Delivery guy information is not found. Please check your connection and try again."
          );
          error.response = {
            data: { message: error.message, type: "error" },
          };
          throw error;
        }

        if (
          !values.branchId ||
          !values.branchName ||
          !values.activeTable ||
          !values.active ||
          !values.activeDailySummery
        ) {
          handleCloseForm();
          const error = new Error(
            "Branch information is not found. Please check your connection, refresh your browser, and try again."
          );
          error.response = {
            data: { message: error.message, type: "error" },
          };
          throw error;
        }

        if (!values.callcenterId) {
          handleCloseForm();
          const error = new Error(
            "Call center information is not found. Please check your connection, refresh your browser, and try again."
          );
          error.response = {
            data: { message: error.message, type: "error" },
          };
          throw error;
        }

        values.status = "Assigned";
        values.fromWhere = fromWhere;
        values.branchKey = values.branchId;
        values.blockHouse = values.blockHouse.toUpperCase();
        values.order = values.order.map((item) => ({
          ...item,
          quantity: parseFloat(item.quantity),
          category: item.isCustom ? "custom" : item.category, 
        }));

        if (values.customerType === "negotiable" && values.price) {
          values.price = parseFloat(values.price);
        } else {
          delete values.price;
        }

        const res = await update(user, data.id, values, "asbeza");
        openSnackbar(`${res.data.message}!`, "success");
        handleCloseForm();
      } catch (error) {
        if (error.response && error.response.data) {
          openSnackbar(
            error.response.data.message,
            error.response.data.type ? error.response.data.type : "error"
          );
        } else {
          openSnackbar(
            "An unexpected error occurred. Please check your connection.",
            "error"
          );
        }
      }
      setIsSubmitting(false);
    },
  });

  // Debounced effect for checking customer registration and block status
  useEffect(() => {
    const handler = setTimeout(() => {
      const checkCustomer = async () => {
        if (!formik.values.blockHouse) {
          setCustomerType("regular");
          setIsMembership(false);
          setIsBlocked(false);
          formik.setFieldValue("customerType", "regular");
          return;
        }

        try {
          // Check block status
          const isBlocked = await checkCustomerBlock(formik.values.blockHouse);
          setIsBlocked(isBlocked);
          if (isBlocked) {
            openSnackbar(
              "This customer is blocked and cannot place orders.",
              "error"
            );
            return;
          }

          // Check membership status
          const isMember = await checkCustomerMembership(
            formik.values.blockHouse
          );
          if (isMember) {
            setCustomerType("membership");
            setIsMembership(true);
            formik.setFieldValue("customerType", "membership");
          } else {
            setCustomerType("regular");
            setIsMembership(false);
            formik.setFieldValue("customerType", "regular");
          }
        } catch (error) {
          console.error("Error checking customer:", error);
          openSnackbar("Failed to check customer status.", "error");
          setCustomerType("regular");
          setIsMembership(false);
          setIsBlocked(false);
          formik.setFieldValue("customerType", "regular");
        }
      };

      checkCustomer();
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(handler);
  }, [formik.values.blockHouse]);

  const handleBranchChange = (event) => {
    const Id = event.target.value;
    const Name = branch.find((branch) => branch[1] === Id)?.[0] || "";
    const ActiveTable = branch.find((branch) => branch[1] === Id)?.[2] || "";
    const Active = branch.find((branch) => branch[1] === Id)?.[3] || "";
    const ActiveDailySummery =
      branch.find((branch) => branch[1] === Id)?.[4] || "";
    formik.setFieldValue("branchId", Id);
    formik.setFieldValue("branchName", Name);
    formik.setFieldValue("activeTable", ActiveTable);
    formik.setFieldValue("active", Active);
    formik.setFieldValue("activeDailySummery", ActiveDailySummery);
  };

  const handleDeliveryGuyChange = (event) => {
    const Id = event.target.value;
    const Name = deliveryman.find((employee) => employee[1] === Id)?.[0] || "";
    formik.setFieldValue("deliveryguyName", Name);
    formik.setFieldValue("deliveryguyId", Id);
  };

  const handleNegotiableChange = (event) => {
    const isChecked = event.target.checked;
    if (!isMembership && !isBlocked) {
      const newType = isChecked ? "negotiable" : "regular";
      setCustomerType(newType);
      formik.setFieldValue("customerType", newType);
      if (!isChecked) {
        formik.setFieldValue("price", "");
      }
    }
  };

  const handleCloseForm = () => {
    closeEditDialog();
    formik.resetForm();
    setCustomerType("regular");
    setIsMembership(false);
    setIsBlocked(false);
  };

  const handleAddOrderItem = () => {
    formik.setFieldValue("order", [
      ...formik.values.order,
      {
        isCustom: false,
        category: "",
        type: "",
        measurement: "",
        quantity: "",
        additionalInfo: "",
      },
    ]);
  };

  const handleRemoveOrderItem = (index) => {
    const newOrder = [...formik.values.order];
    newOrder.splice(index, 1);
    formik.setFieldValue("order", newOrder);
  };

  const handleOrderItemChange = (index, field, value) => {
    const newOrder = [...formik.values.order];
    newOrder[index] = { ...newOrder[index], [field]: value };

    if (field === "isCustom") {
      // Reset fields when toggling custom mode
      newOrder[index].category = "";
      newOrder[index].type = "";
      newOrder[index].measurement = "";
      newOrder[index].additionalInfo = "";
    } else if (field === "category" && !newOrder[index].isCustom) {
      newOrder[index].type = "";
      newOrder[index].measurement = "";
      newOrder[index].additionalInfo = "";
    } else if (field === "type" && !newOrder[index].isCustom) {
      // Automatically set the measurement for predefined orders
      const selectedCategory = categories.find(
        (cat) => cat.name === newOrder[index].category
      );
      const selectedType = selectedCategory?.orderTypes.find(
        (t) => t.name === value
      );
      newOrder[index].measurement = selectedType?.measurement || "";
      newOrder[index].additionalInfo = "";
    }

    formik.setFieldValue("order", newOrder);
  };

  const branch = branches?.map((item) => [
    item.name,
    item.id,
    item.activeTable,
    item.active,
    item.activeDailySummery,
  ]);

  const deliveryMan = deliveryGuy
    ? deliveryGuy[formik.values.branchId ? formik.values.branchId : ""]
    : [];
  const deliveryman = deliveryMan?.map((item) => [
    item.deliveryGuyName,
    item.deliveryManId,
  ]);

  useEffect(() => {
    const { branchId, activeTable, branchName } = formik.values;
    if (branchId && !activeTable) {
      openSnackbar(
        `${branchName} branch does not have a daily table today. So you can't edit an order. Please inform the branch to create a daily table in their sheet.`,
        "info"
      );
    }
  }, [formik.values, openSnackbar]);

  return (
    <div>
      <Dialog
        open={isEditDialogOpen}
        onClose={handleCloseForm}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: theme.palette.background.alt }}>
          Edit Asbeza Order
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: theme.palette.background.alt }}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              {/* Name */}
              <CustomTextField
                name="name"
                label="Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={
                  formik.touched.name && formik.errors.name
                    ? formik.errors.name
                    : ""
                }
                touched={formik.touched.name}
                disabled={isBlocked}
              />

              {/* Phone */}
              <CustomTextField
                name="phone"
                label="Phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={
                  formik.touched.phone && formik.errors.phone
                    ? formik.errors.phone
                    : ""
                }
                touched={formik.touched.phone}
                disabled={isBlocked}
              />

              {/* Block/House */}
              <CustomTextField
                name="blockHouse"
                label="Block/House"
                value={formik.values.blockHouse}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.blockHouse && Boolean(formik.errors.blockHouse)
                }
                helperText={
                  formik.touched.blockHouse && formik.errors.blockHouse
                    ? formik.errors.blockHouse
                    : ""
                }
                touched={formik.touched.blockHouse}
                disabled={isBlocked}
              />

              {/* Customer Type and Block Status Display */}
              <Grid item xs={12}>
                <Typography variant="subtitle1">
                  Customer Type:{" "}
                  {customerType.charAt(0).toUpperCase() + customerType.slice(1)}
                </Typography>
                {isBlocked && (
                  <Typography variant="subtitle1" color="error">
                    Customer is blocked and cannot place orders.
                  </Typography>
                )}
              </Grid>

              {/* Negotiable Checkbox */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={customerType === "negotiable"}
                      onChange={handleNegotiableChange}
                      disabled={isMembership || isBlocked}
                      color="primary"
                    />
                  }
                  label="Negotiable Order"
                />
              </Grid>

              {/* Price (for Negotiable) */}
              {customerType === "negotiable" && (
                <CustomTextField
                  name="price"
                  label="Custom Price"
                  type="number"
                  value={formik.values.price}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.price && Boolean(formik.errors.price)}
                  helperText={
                    formik.touched.price && formik.errors.price
                      ? formik.errors.price
                      : ""
                  }
                  touched={formik.touched.price}
                  disabled={isBlocked}
                />
              )}

              {/* Branch */}
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormControl
                  fullWidth
                  error={
                    formik.touched.branchId && Boolean(formik.errors.branchId)
                  }
                >
                  <InputLabel
                    shrink
                    style={{
                      marginTop: "8px",
                      color: theme.palette.secondary[100],
                    }}
                  >
                    Branch
                  </InputLabel>
                  <Select
                    name="branchId"
                    value={formik.values.branchId}
                    onChange={handleBranchChange}
                    onBlur={formik.handleBlur}
                    label="Branch"
                    disabled={isBlocked}
                  >
                    <MenuItem value="">Select Branch</MenuItem>
                    {branch?.map((branch) => (
                      <MenuItem key={branch[1]} value={branch[1]}>
                        {branch[0]}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.branchId && formik.errors.branchId && (
                    <Typography
                      variant="caption"
                      color="error"
                      style={{ marginTop: "4px" }}
                    >
                      {formik.errors.branchId}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Delivery Guy */}
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <FormControl
                  fullWidth
                  error={
                    formik.touched.deliveryguyId &&
                    Boolean(formik.errors.deliveryguyId)
                  }
                >
                  <InputLabel
                    shrink
                    style={{
                      marginTop: "8px",
                      color: theme.palette.secondary[100],
                    }}
                  >
                    Delivery Guy
                  </InputLabel>
                  <Select
                    name="deliveryguyId"
                    value={formik.values.deliveryguyId}
                    onChange={handleDeliveryGuyChange}
                    onBlur={formik.handleBlur}
                    label="Delivery Guy"
                    disabled={isBlocked}
                  >
                    <MenuItem value="">Select Delivery Guy</MenuItem>
                    {deliveryman?.map((branch) => (
                      <MenuItem key={branch[1]} value={branch[1]}>
                        {branch[0]}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.deliveryguyId &&
                    formik.errors.deliveryguyId && (
                      <Typography
                        variant="caption"
                        color="error"
                        style={{ marginTop: "4px" }}
                      >
                        {formik.errors.deliveryguyId}
                      </Typography>
                    )}
                </FormControl>
              </Grid>

              {/* Additional Info (for entire order) */}
              <CustomTextField
                name="additionalInfo"
                label="Additional Info"
                value={formik.values.additionalInfo}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.additionalInfo &&
                  Boolean(formik.errors.additionalInfo)
                }
                helperText={
                  formik.touched.additionalInfo && formik.errors.additionalInfo
                    ? formik.errors.additionalInfo
                    : ""
                }
                touched={formik.touched.additionalInfo}
                required={false}
                disabled={isBlocked}
              />

              {/* Order Items */}
              {formik.values.order.map((item, index) => (
                <Grid item xs={12} key={index}>
                  <Grid container spacing={1} alignItems="center">
                    {/* Custom Order Checkbox */}
                    <Grid item xs={12} sm={1} md={1} lg={1}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <Typography
                          variant="caption"
                          color={theme.palette.secondary[100]}
                          sx={{ mb: 0.5 }}
                        >
                          Custom
                        </Typography>
                        <Checkbox
                          checked={item.isCustom}
                          onChange={(e) =>
                            handleOrderItemChange(
                              index,
                              "isCustom",
                              e.target.checked
                            )
                          }
                          disabled={isBlocked}
                          color="primary"
                          size="small"
                        />
                      </Box>
                    </Grid>

                    {/* Category (only for predefined orders) */}
                    {!item.isCustom && (
                      <Grid item xs={12} sm={2} md={2} lg={2}>
                        <FormControl
                          fullWidth
                          error={
                            formik.touched.order?.[index]?.category &&
                            Boolean(formik.errors.order?.[index]?.category)
                          }
                          disabled={isBlocked}
                        >
                          <InputLabel
                            shrink
                            style={{
                              marginTop: "8px",
                              color: theme.palette.secondary[100],
                            }}
                          >
                            Category {index + 1}
                          </InputLabel>
                          <Select
                            name={`order[${index}].category`}
                            value={item.category}
                            onChange={(e) =>
                              handleOrderItemChange(
                                index,
                                "category",
                                e.target.value
                              )
                            }
                            onBlur={formik.handleBlur}
                            label={`Category ${index + 1}`}
                          >
                            <MenuItem value="">Select Category</MenuItem>
                            {categories.map((category) => (
                              <MenuItem
                                key={category.name}
                                value={category.name}
                              >
                                {category.name.charAt(0).toUpperCase() +
                                  category.name.slice(1)}
                              </MenuItem>
                            ))}
                          </Select>
                          {formik.touched.order?.[index]?.category &&
                            formik.errors.order?.[index]?.category && (
                              <Typography
                                variant="caption"
                                color="error"
                                style={{ marginTop: "4px" }}
                              >
                                {formik.errors.order[index].category}
                              </Typography>
                            )}
                        </FormControl>
                      </Grid>
                    )}

                    {/* Order Type */}
                    <Grid item xs={12} sm={2} md={2} lg={2}>
                      {item.isCustom ? (
                        <TextField
                          name={`order[${index}].type`}
                          label="Custom Order Type"
                          value={item.type}
                          onChange={(e) =>
                            handleOrderItemChange(index, "type", e.target.value)
                          }
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.order?.[index]?.type &&
                            Boolean(formik.errors.order?.[index]?.type)
                          }
                          helperText={
                            formik.touched.order?.[index]?.type &&
                            formik.errors.order?.[index]?.type
                              ? formik.errors.order[index].type
                              : ""
                          }
                          fullWidth
                          disabled={isBlocked}
                          InputLabelProps={{
                            shrink: true,
                            style: { color: theme.palette.secondary[100] },
                          }}
                        />
                      ) : (
                        <FormControl
                          fullWidth
                          error={
                            formik.touched.order?.[index]?.type &&
                            Boolean(formik.errors.order?.[index]?.type)
                          }
                          disabled={!item.category || isBlocked}
                        >
                          <InputLabel
                            shrink
                            style={{
                              marginTop: "8px",
                              color: theme.palette.secondary[100],
                            }}
                          >
                            Order Type {index + 1}
                          </InputLabel>
                          <Select
                            name={`order[${index}].type`}
                            value={item.type}
                            onChange={(e) =>
                              handleOrderItemChange(
                                index,
                                "type",
                                e.target.value
                              )
                            }
                            onBlur={formik.handleBlur}
                            label={`Order Type ${index + 1}`}
                          >
                            <MenuItem value="">Select Type</MenuItem>
                            {categories
                              .find((cat) => cat.name === item.category)
                              ?.orderTypes.map((type) => (
                                <MenuItem key={type.name} value={type.name}>
                                  {type.name.charAt(0).toUpperCase() +
                                    type.name.slice(1)}
                                </MenuItem>
                              ))}
                          </Select>
                          {formik.touched.order?.[index]?.type &&
                            formik.errors.order?.[index]?.type && (
                              <Typography
                                variant="caption"
                                color="error"
                                style={{ marginTop: "4px" }}
                              >
                                {formik.errors.order[index].type}
                              </Typography>
                            )}
                        </FormControl>
                      )}
                    </Grid>

                    {/* Measurement */}
                    <Grid item xs={12} sm={2} md={2} lg={2}>
                      {item.isCustom ? (
                        <TextField
                          name={`order[${index}].measurement`}
                          label="Custom Measurement"
                          value={item.measurement}
                          onChange={(e) =>
                            handleOrderItemChange(
                              index,
                              "measurement",
                              e.target.value
                            )
                          }
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.order?.[index]?.measurement &&
                            Boolean(formik.errors.order?.[index]?.measurement)
                          }
                          helperText={
                            formik.touched.order?.[index]?.measurement &&
                            formik.errors.order?.[index]?.measurement
                              ? formik.errors.order[index].measurement
                              : ""
                          }
                          fullWidth
                          disabled={isBlocked}
                          InputLabelProps={{
                            shrink: true,
                            style: { color: theme.palette.secondary[100] },
                          }}
                        />
                      ) : (
                        <Typography
                          variant="body1"
                          style={{
                            marginTop: "8px",
                            color: item.type
                              ? theme.palette.secondary[100]
                              : theme.palette.text.disabled,
                          }}
                        >
                          Measurement:{" "}
                          {item.type
                            ? categories
                                .find((cat) => cat.name === item.category)
                                ?.orderTypes.find((t) => t.name === item.type)
                                ?.measurement.charAt(0)
                                .toUpperCase() +
                                categories
                                  .find((cat) => cat.name === item.category)
                                  ?.orderTypes.find((t) => t.name === item.type)
                                  ?.measurement.slice(1) || "N/A"
                            : "Select an order type"}
                        </Typography>
                      )}
                      {formik.touched.order?.[index]?.measurement &&
                        formik.errors.order?.[index]?.measurement && (
                          <Typography
                            variant="caption"
                            color="error"
                            style={{ marginTop: "4px" }}
                          >
                            {formik.errors.order[index].measurement}
                          </Typography>
                        )}
                    </Grid>

                    {/* Quantity */}
                    <Grid item xs={12} sm={2} md={2} lg={2}>
                      <TextField
                        name={`order[${index}].quantity`}
                        label="Quantity"
                        type="number"
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) =>
                          handleOrderItemChange(
                            index,
                            "quantity",
                            e.target.value
                          )
                        }
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.order?.[index]?.quantity &&
                          Boolean(formik.errors.order?.[index]?.quantity)
                        }
                        helperText={
                          formik.touched.order?.[index]?.quantity &&
                          formik.errors.order?.[index]?.quantity
                            ? formik.errors.order[index].quantity
                            : ""
                        }
                        fullWidth
                        disabled={isBlocked}
                        InputLabelProps={{
                          shrink: true,
                          style: { color: theme.palette.secondary[100] },
                        }}
                      />
                    </Grid>

                    {/* Additional Info (for order item) */}
                    <Grid item xs={12} sm={2} md={2} lg={2}>
                      <TextField
                        name={`order[${index}].additionalInfo`}
                        label="Additional Info"
                        value={item.additionalInfo || ""}
                        onChange={(e) =>
                          handleOrderItemChange(
                            index,
                            "additionalInfo",
                            e.target.value
                          )
                        }
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.order?.[index]?.additionalInfo &&
                          Boolean(formik.errors.order?.[index]?.additionalInfo)
                        }
                        helperText={
                          formik.touched.order?.[index]?.additionalInfo &&
                          formik.errors.order?.[index]?.additionalInfo
                            ? formik.errors.order[index].additionalInfo
                            : ""
                        }
                        fullWidth
                        disabled={isBlocked}
                        InputLabelProps={{
                          shrink: true,
                          style: { color: theme.palette.secondary[100] },
                        }}
                      />
                    </Grid>

                    {/* Remove Button */}
                    <Grid item xs={12} sm={1} md={1} lg={1}>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleRemoveOrderItem(index)}
                        sx={{ height: "100%" }}
                        disabled={isBlocked}
                      >
                        Remove
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              ))}

              {/* Add Order Button */}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleAddOrderItem}
                  disabled={isBlocked}
                >
                  Add Order Item
                </Button>
              </Grid>

              {/* Form Actions */}
              <Grid
                item
                xs={12}
                container
                alignItems="end"
                justifyContent="end"
              >
                <DialogActions
                  sx={{ backgroundColor: theme.palette.background.alt }}
                >
                  <Button
                    variant="contained"
                    onClick={handleCloseForm}
                    color="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className="submit-button"
                    disabled={isBlocked || formik.isSubmitting}
                  >
                    Submit
                  </Button>
                </DialogActions>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EditAsbezaOrderForm;
